import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import Container from "@/components/containers/Container";
import Tab from "@/components/containers/tabs/Tab";
import TabGroup from "@/components/containers/tabs/TabGroup";
import TabList from "@/components/containers/tabs/TabList";
import TabPanel from "@/components/containers/tabs/TabPanel";
import TabPanels from "@/components/containers/tabs/TabPanels";
import type { ChildRouteContext } from "@/root";
import { throwIfHasNoPermission } from "@/utils/permissions";

import ApplicationList from "@/modules/application/admin/components/ApplicationList";
import { fetchApplicationVersionsQueryOptions } from "@/modules/application/admin/services";
import { applicationStatuses } from "@/modules/application/shared/constants";

const searchSchema = z.object({
  status: z.enum(applicationStatuses).catch("all"),
});

export const Route = createFileRoute(
  "/admin/_panel/_application/application/applications/",
)({
  validateSearch: (search) => searchSchema.parse(search),
  beforeLoad: ({ context }) => {
    throwIfHasNoPermission(context.user, "application.application.view");

    return {
      crumb: [
        {
          label: context.t("Manage Processes", { ns: "application.admin" }),
        },
      ],
    } satisfies ChildRouteContext;
  },
  loader: (opts) =>
    opts.context.queryClient.prefetchQuery(
      fetchApplicationVersionsQueryOptions(),
    ),
  component: Component,
});

function Component() {
  const { t } = useTranslation("application.admin");

  const { status } = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <Container
      title={t("Manage Processes")}
      description={t("Manage, create and edit processes")}
    >
      <TabGroup
        defaultIndex={applicationStatuses.indexOf(status)}
        onChange={(index) =>
          navigate({ search: { status: applicationStatuses[index] } })
        }
      >
        <TabList>
          <Tab>{t("All", { ns: "core.shared" })}</Tab>
          <Tab>{t("Published", { ns: "core.shared" })}</Tab>
          <Tab>{t("Drafts", { ns: "core.shared" })}</Tab>
          <Tab>{t("Inactives", { ns: "core.shared" })}</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <ApplicationList status="all" />
          </TabPanel>
          <TabPanel>
            <ApplicationList status="published" />
          </TabPanel>
          <TabPanel>
            <ApplicationList status="draft" />
          </TabPanel>
          <TabPanel>
            <ApplicationList status="inactive" />
          </TabPanel>
        </TabPanels>
      </TabGroup>
    </Container>
  );
}
